import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, HelpCircle, Minus, GripVertical } from 'lucide-react';
import { useDraggablePanel } from '@/hooks/useDraggablePanel';

/**
 * Small onboarding card that explains the 3D scene controls.
 * Draggable + minimizable so it never blocks the map/controls.
 * Position + minimized state are persisted in localStorage.
 */
export default function WelcomePanel({ onClose }) {
  const { t } = useTranslation();
  const initialX = typeof window !== 'undefined' ? Math.max(16, window.innerWidth - 296) : 16;
  const { ref, pos, minimized, dragging, toggleMin, dragHandleProps } = useDraggablePanel('welcome', { x: initialX, y: 96 });

  return (
    <div
      ref={ref}
      className={`fixed top-0 left-0 z-[45] ${dragging ? '' : 'transition-transform duration-150 ease-out'}`}
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
      data-testid="welcome-panel"
    >
      {minimized ? (
        <button
          onClick={toggleMin}
          className="w-11 h-11 rounded-full bg-emerald-800/90 backdrop-blur-xl border border-emerald-500/40 shadow-2xl grid place-items-center text-emerald-200 hover:text-white hover:scale-105 transition-transform"
          title="Show tips"
          data-testid="welcome-restore-btn"
          {...dragHandleProps}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      ) : (
        <div className="max-w-xs bg-gradient-to-br from-emerald-900/90 to-emerald-800/80 backdrop-blur-xl rounded-2xl border border-emerald-500/30 shadow-xl overflow-hidden">
          {/* Drag handle bar */}
          <div className="flex items-center justify-between px-2 pt-1.5 pb-0.5 bg-black/20" {...dragHandleProps}>
            <GripVertical className="w-3.5 h-3.5 text-emerald-300/60" />
            <div className="flex items-center gap-0.5">
              <button
                onClick={(e) => { e.stopPropagation(); toggleMin(); }}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded hover:bg-emerald-800/50 text-emerald-200 hover:text-white transition-colors"
                title="Minimize"
                data-testid="welcome-minimize-btn"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              {onClose && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClose(); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 rounded hover:bg-emerald-800/50 text-emerald-200 hover:text-white transition-colors"
                  data-testid="close-welcome-btn"
                  aria-label="Close welcome"
                  title="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <img src="/hln-logo.png" alt="HLN Logo" className="w-8 h-8 object-contain drop-shadow" />
              <div>
                <h4 className="text-white font-black text-sm leading-tight">{t('welcome.greeting')}</h4>
                <p className="text-emerald-300 text-[10px]">{t('welcome.subtitle')}</p>
              </div>
            </div>
            <ul className="text-xs text-emerald-100 space-y-1.5 mt-3">
              <li>🖱️ <span className="text-emerald-300">{t('welcome.drag').split(':')[0]}:</span> {t('welcome.drag').split(':').slice(1).join(':').trim()}</li>
              <li>📜 <span className="text-emerald-300">{t('welcome.scroll').split(':')[0]}:</span> {t('welcome.scroll').split(':').slice(1).join(':').trim()}</li>
              <li>✋ <span className="text-emerald-300">{t('welcome.right_drag').split(':')[0]}:</span> {t('welcome.right_drag').split(':').slice(1).join(':').trim()}</li>
              <li>🎯 <span className="text-emerald-300">{t('welcome.click').split(':')[0]}:</span> {t('welcome.click').split(':').slice(1).join(':').trim()}</li>
              <li>☔ <span className="text-emerald-300">{t('welcome.rain').split(':')[0]}:</span> {t('welcome.rain').split(':').slice(1).join(':').trim()}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
